import { useRef } from 'react';
import { getAllTabbableIn } from 'ariakit-utils/focus';
import { useWrapElement, useForkRef } from 'ariakit-utils/hooks';
import { createHook, createComponent, createElement } from 'ariakit-utils/system';
import { FocusTrap } from './focus-trap.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

const useFocusTrapRegion = createHook(_ref => {
  let {
    enabled = false,
    ...props
  } = _ref;
  const ref = useRef(null);
  props = useWrapElement(props, element => {
    const renderFocusTrap = () => {
      if (!enabled) return null;
      return /*#__PURE__*/jsx(FocusTrap, {
        onFocus: event => {
          const container = ref.current;
          if (!container) return;
          const tabbables = getAllTabbableIn(container, true);
          const first = tabbables[0];
          const last = tabbables[tabbables.length - 1]; // Fallbacks to the container element

          if (!tabbables.length) {
            container.focus();
            return;
          }

          if (event.relatedTarget === first) {
            last == null ? void 0 : last.focus();
          } else {
            first == null ? void 0 : first.focus();
          }
        }
      });
    };

    return /*#__PURE__*/jsxs(Fragment, {
      children: [renderFocusTrap(), element, renderFocusTrap()]
    });
  }, [enabled]);
  props = { ...props,
    ref: useForkRef(ref, props.ref)
  };
  return props;
});
/**
 * A component that renders a focus trap region element.
 * @see https://ariakit.org/components/focus-trap-region
 * @example
 * ```jsx
 * <FocusTrapRegion>
 *  <Button>click me</Button>
 *  <Button>trap focus</Button>
 *  <Button disabled>disabled Button</Button>
 * </FocusTrapRegion>
 * ```
 */

const FocusTrapRegion = createComponent(props => {
  const htmlProps = useFocusTrapRegion(props);
  return createElement("div", htmlProps);
});

if (process.env.NODE_ENV !== "production") {
  FocusTrapRegion.displayName = "FocusTrapRegion";
}

export { FocusTrapRegion, useFocusTrapRegion };
